import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import {
  useAgentActions,
  useAgentState,
} from '#/features/terminal/agent-context';
import { useTerminalDrawerActions } from '#/features/terminal/drawer-context';

interface CronJob {
  readonly id: string;
  readonly name: string;
  readonly schedule: string | null;
  readonly command: string | null;
  readonly description: string | null;
  readonly enabled: boolean | null;
  readonly status: string | null;
  readonly nextRun: string | null;
  readonly lastRun: string | null;
}

interface CronRun {
  readonly id: string;
  readonly status: string | null;
  readonly startedAt: string | null;
  readonly finishedAt: string | null;
  readonly summary: string | null;
  readonly error: string | null;
}

interface CronStatus {
  readonly label: string;
  readonly detail: string | null;
}

const JOB_ARRAY_KEYS = [
  'jobs',
  'items',
  'crons',
  'schedules',
  'tasks',
] as const;
const RUN_ARRAY_KEYS = ['runs', 'items', 'history', 'executions'] as const;

export default function CronView() {
  const actions = useAgentActions();
  const agentState = useAgentState();
  const drawerActions = useTerminalDrawerActions();
  const isReady = agentState.status === 'authenticated';
  const [jobs, setJobs] = useState<readonly CronJob[]>([]);
  const [status, setStatus] = useState<CronStatus | null>(null);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [runs, setRuns] = useState<readonly CronRun[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingRuns, setIsLoadingRuns] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [runsError, setRunsError] = useState<string | null>(null);

  const selectedJob = useMemo(
    () => jobs.find((job) => job.id === selectedJobId) ?? jobs[0] ?? null,
    [jobs, selectedJobId],
  );

  const loadCronJobs = useCallback(async () => {
    if (!isReady) return;
    setIsLoading(true);
    setError(null);

    const [statusResult, listResult] = await Promise.allSettled([
      actions.request<unknown>('cron.status'),
      actions.request<unknown>('cron.list'),
    ]);

    const statusPayload =
      statusResult.status === 'fulfilled' ? statusResult.value : null;
    const listPayload =
      listResult.status === 'fulfilled' ? listResult.value : null;
    const nextJobs = mergeCronJobs(
      normalizeCronJobs(listPayload),
      normalizeCronJobs(statusPayload),
    );

    setStatus(normalizeCronStatus(statusPayload));
    setJobs(nextJobs);
    setSelectedJobId((current) => {
      if (nextJobs.length === 0) return null;
      if (current != null && nextJobs.some((job) => job.id === current)) {
        return current;
      }
      return nextJobs[0]?.id ?? null;
    });

    if (listResult.status === 'rejected' && nextJobs.length === 0) {
      setError(getErrorMessage(listResult.reason, 'Unable to load cron jobs.'));
    }
    if (
      statusResult.status === 'rejected' &&
      listResult.status === 'rejected'
    ) {
      setError(getErrorMessage(statusResult.reason, 'Cron RPC unavailable.'));
    }
    setIsLoading(false);
  }, [actions, isReady]);

  useEffect(() => {
    if (!isReady) {
      setJobs([]);
      setStatus(null);
      setSelectedJobId(null);
      setRuns([]);
      setError(null);
      return;
    }
    loadCronJobs().catch((err: unknown) => {
      setError(getErrorMessage(err, 'Unable to load cron jobs.'));
    });
  }, [isReady, loadCronJobs]);

  useEffect(() => {
    if (!isReady || selectedJob == null) {
      setRuns([]);
      setRunsError(null);
      return;
    }

    let cancelled = false;
    setIsLoadingRuns(true);
    setRunsError(null);
    actions
      .request<unknown>('cron.runs', { jobId: selectedJob.id, limit: 20 })
      .catch(async () =>
        actions.request<unknown>('cron.runs', {
          id: selectedJob.id,
          limit: 20,
        }),
      )
      .catch(async () =>
        actions.request<unknown>('cron.runs', { jobId: selectedJob.id }),
      )
      .catch(async () =>
        actions.request<unknown>('cron.runs', { id: selectedJob.id }),
      )
      .then((payload) => {
        if (cancelled) return;
        const nextRuns = normalizeCronRuns(payload);
        setRuns(nextRuns);
        setJobs((prev) =>
          prev.map((job) =>
            job.id === selectedJob.id ? enrichJobFromRuns(job, nextRuns) : job,
          ),
        );
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setRuns([]);
        setRunsError(getErrorMessage(err, 'Unable to load runs.'));
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoadingRuns(false);
      });

    return () => {
      cancelled = true;
    };
  }, [actions, isReady, selectedJob]);

  const askAgentAboutJob = useCallback(
    (job: CronJob, intent: 'run' | 'stop') => {
      const action =
        intent === 'run'
          ? 'Run this OpenClaw cron job now and summarize the result.'
          : 'Review this OpenClaw cron job and disable or cancel it if it is safe to do so.';
      actions.sendMessage(`${action}\n\nJob: ${job.name}\njobId: ${job.id}`);
      drawerActions.setActiveTab('chat');
    },
    [actions, drawerActions],
  );

  return (
    <Scroll>
      <Header>
        <HeaderText>
          <Title>Cron jobs</Title>
          {status != null && (
            <Subtitle>
              {status.label}
              {status.detail != null ? ` - ${status.detail}` : ''}
            </Subtitle>
          )}
        </HeaderText>
        <RefreshButton
          type="button"
          disabled={!isReady || isLoading}
          onClick={() => {
            loadCronJobs().catch((err: unknown) => {
              setError(getErrorMessage(err, 'Unable to load cron jobs.'));
            });
          }}
          title={isReady ? 'Refresh cron jobs' : 'Not connected'}
        >
          Refresh
        </RefreshButton>
      </Header>

      {!isReady && <Notice>Connect to the agent first.</Notice>}
      {error != null && <Notice>{error}</Notice>}

      {isReady && isLoading && jobs.length === 0 && (
        <EmptyState>Loading cron jobs...</EmptyState>
      )}

      {isReady && !isLoading && error == null && jobs.length === 0 && (
        <EmptyState>No active cron jobs.</EmptyState>
      )}

      {jobs.length > 0 && (
        <JobList>
          {jobs.map((job) => (
            <JobCard
              key={job.id}
              role="button"
              tabIndex={0}
              $selected={job.id === selectedJob?.id}
              onClick={() => setSelectedJobId(job.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelectedJobId(job.id);
                }
              }}
            >
              <JobTopLine>
                <JobName>{job.name}</JobName>
                <StatusBadge $enabled={job.enabled}>
                  {formatJobBadge(job)}
                </StatusBadge>
              </JobTopLine>
              {job.description != null && (
                <JobDescription>{job.description}</JobDescription>
              )}
              <MetaGrid>
                <MetaItem>
                  <MetaLabel>Schedule</MetaLabel>
                  <MetaValue>{job.schedule ?? '-'}</MetaValue>
                </MetaItem>
                <MetaItem>
                  <MetaLabel>Next</MetaLabel>
                  <MetaValue>{formatMaybeDate(job.nextRun)}</MetaValue>
                </MetaItem>
                <MetaItem>
                  <MetaLabel>Last</MetaLabel>
                  <MetaValue>{formatMaybeDate(job.lastRun)}</MetaValue>
                </MetaItem>
              </MetaGrid>
              {job.command != null && <CommandLine>{job.command}</CommandLine>}
              <JobActions>
                <InlineActionButton
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    askAgentAboutJob(job, 'run');
                  }}
                >
                  Ask run
                </InlineActionButton>
                <InlineActionButton
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    askAgentAboutJob(job, 'stop');
                  }}
                >
                  Ask stop
                </InlineActionButton>
              </JobActions>
            </JobCard>
          ))}
        </JobList>
      )}

      {selectedJob != null && (
        <RunsPanel>
          <PanelTitle>Recent runs</PanelTitle>
          {isLoadingRuns && runs.length === 0 && (
            <EmptyState>Loading runs...</EmptyState>
          )}
          {runsError != null && <RunNotice>{runsError}</RunNotice>}
          {!isLoadingRuns && runsError == null && runs.length === 0 && (
            <RunNotice>No recorded runs.</RunNotice>
          )}
          {runs.map((run) => (
            <RunRow key={run.id}>
              <RunStatus>{run.status ?? 'unknown'}</RunStatus>
              <RunBody>
                <RunTime>
                  {formatMaybeDate(run.startedAt)}
                  {run.finishedAt != null
                    ? ` - ${formatMaybeDate(run.finishedAt)}`
                    : ''}
                </RunTime>
                {run.summary != null && <RunText>{run.summary}</RunText>}
                {run.error != null && <RunError>{run.error}</RunError>}
              </RunBody>
            </RunRow>
          ))}
        </RunsPanel>
      )}
    </Scroll>
  );
}

export function normalizeCronStatus(payload: unknown): CronStatus | null {
  const record = asRecord(payload);
  if (record == null) return null;

  const label =
    pickString(record, ['status', 'state', 'scheduler_state']) ??
    (pickBoolean(record, ['enabled', 'running', 'active']) === false
      ? 'disabled'
      : 'active');
  const detail = pickString(record, [
    'detail',
    'message',
    'version',
    'scheduler',
    'service',
  ]);
  return { label, detail };
}

export function normalizeCronJobs(payload: unknown): readonly CronJob[] {
  const items = extractArray(payload, JOB_ARRAY_KEYS);
  return items
    .map(asRecord)
    .filter((record): record is Record<string, unknown> => record != null)
    .map((record, index) => {
      const id =
        pickString(record, ['id', 'job_id', 'jobId', 'name', 'slug']) ??
        `cron-${index + 1}`;
      return {
        id,
        name:
          pickString(record, ['name', 'title', 'slug', 'skill', 'command']) ??
          id,
        schedule: formatSchedule(record),
        command: pickString(record, [
          'command',
          'cmd',
          'prompt',
          'message',
          'skill',
          'task',
          'payload.message',
          'payload.prompt',
          'payload.systemEvent',
        ]),
        description: pickString(record, ['description', 'summary']),
        enabled: pickBoolean(record, ['enabled', 'active', 'running']),
        status:
          pickString(record, [
            'status',
            'phase',
            'state.status',
            'state.state',
            'state.phase',
            'runtime.status',
            'runtime.state',
            'last.status',
            'last.result',
          ]) ?? statusFromBoolean(record),
        nextRun: pickString(record, [
          'next_run',
          'nextRun',
          'next_at',
          'nextAt',
          'next',
          'state.nextRun',
          'state.next_run',
          'state.nextAt',
          'runtime.nextRun',
          'runtime.next_run',
        ]),
        lastRun: pickString(record, [
          'last_run',
          'lastRun',
          'last_at',
          'lastAt',
          'last',
          'last.startedAt',
          'last.finishedAt',
          'last.endedAt',
          'state.lastRun',
          'state.last_run',
          'state.lastAt',
          'runtime.lastRun',
          'runtime.last_run',
        ]),
      };
    });
}

export function normalizeCronRuns(payload: unknown): readonly CronRun[] {
  const items = extractArray(payload, RUN_ARRAY_KEYS);
  return items
    .map(asRecord)
    .filter((record): record is Record<string, unknown> => record != null)
    .map((record, index) => ({
      id: pickString(record, ['id', 'run_id', 'runId']) ?? `run-${index + 1}`,
      status:
        pickString(record, [
          'status',
          'state',
          'result',
          'phase',
          'delivery.status',
          'delivery.delivered',
        ]) ?? statusFromBoolean(record),
      startedAt: pickString(record, [
        'started_at',
        'startedAt',
        'start_time',
        'startTime',
        'created_at',
        'createdAt',
        'time',
        'timestamp',
      ]),
      finishedAt: pickString(record, [
        'finished_at',
        'finishedAt',
        'completed_at',
        'completedAt',
        'end_time',
        'endTime',
        'ended_at',
        'endedAt',
      ]),
      summary: pickString(record, [
        'summary',
        'message',
        'output',
        'text',
        'resultText',
        'delivery.summary',
      ]),
      error: pickString(record, [
        'error',
        'error_message',
        'errorMessage',
        'reason',
        'delivery.error',
      ]),
    }));
}

export function mergeCronJobs(
  primary: readonly CronJob[],
  secondary: readonly CronJob[],
): readonly CronJob[] {
  const merged = new Map<string, CronJob>();
  for (const job of [...secondary, ...primary]) {
    const existing = merged.get(job.id);
    if (existing == null) {
      merged.set(job.id, job);
      continue;
    }
    merged.set(job.id, {
      id: existing.id,
      name: existing.name !== existing.id ? existing.name : job.name,
      schedule: job.schedule ?? existing.schedule,
      command: job.command ?? existing.command,
      description: job.description ?? existing.description,
      enabled: job.enabled ?? existing.enabled,
      status: job.status ?? existing.status,
      nextRun: job.nextRun ?? existing.nextRun,
      lastRun: job.lastRun ?? existing.lastRun,
    });
  }
  return [...merged.values()];
}

function enrichJobFromRuns(job: CronJob, runs: readonly CronRun[]): CronJob {
  const latest = runs[0];
  if (latest == null) return job;
  return {
    ...job,
    status: latest.status ?? job.status,
    lastRun: job.lastRun ?? latest.finishedAt ?? latest.startedAt,
  };
}

function extractArray(
  payload: unknown,
  keys: readonly string[],
): readonly unknown[] {
  if (Array.isArray(payload)) return payload;
  const record = asRecord(payload);
  if (record == null) return [];
  for (const key of keys) {
    const value = record[key];
    if (Array.isArray(value)) return value;
    const nested = asRecord(value);
    if (nested != null) {
      const nestedItems = extractArray(nested, keys);
      if (nestedItems.length > 0) return nestedItems;
    }
  }
  for (const value of Object.values(record)) {
    const nested = asRecord(value);
    if (nested == null) continue;
    const nestedItems = extractArray(nested, keys);
    if (nestedItems.length > 0) return nestedItems;
  }
  return [];
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

function pickString(
  record: Record<string, unknown>,
  keys: readonly string[],
): string | null {
  for (const key of keys) {
    const value = readPath(record, key);
    if (typeof value === 'string' && value.trim().length > 0) {
      return value;
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
  }
  return null;
}

function pickBoolean(
  record: Record<string, unknown>,
  keys: readonly string[],
): boolean | null {
  for (const key of keys) {
    const value = readPath(record, key);
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
      if (value.toLowerCase() === 'true') return true;
      if (value.toLowerCase() === 'false') return false;
    }
  }
  return null;
}

function readPath(record: Record<string, unknown>, path: string): unknown {
  if (!path.includes('.')) return record[path];
  return path.split('.').reduce<unknown>((current, part) => {
    const currentRecord = asRecord(current);
    if (currentRecord == null) return undefined;
    return currentRecord[part];
  }, record);
}

function formatSchedule(record: Record<string, unknown>): string | null {
  const schedule = asRecord(record.schedule);
  if (schedule != null) {
    const kind = pickString(schedule, ['kind', 'type']);
    if (kind === 'at') {
      const at = pickString(schedule, ['at', 'time']);
      return at != null ? `at ${formatMaybeDate(at)}` : 'at';
    }
    if (kind === 'every') {
      const every = pickString(schedule, ['every', 'everyMs', 'ms']);
      return every != null ? `every ${formatDuration(every)}` : 'every';
    }
    if (kind === 'cron') {
      const expr = pickString(schedule, ['expr', 'cron', 'expression']);
      const tz = pickString(schedule, ['tz', 'timezone']);
      if (expr == null) return 'cron';
      return tz != null ? `${expr} (${tz})` : expr;
    }
  }

  const direct = pickString(record, [
    'cron',
    'expr',
    'expression',
    'cadence',
    'interval',
    'schedule.expr',
    'schedule.cron',
    'schedule.expression',
  ]);
  if (direct != null) return direct;
  return pickString(record, ['schedule']);
}

function formatDuration(value: string): string {
  const ms = Number(value);
  if (!Number.isFinite(ms)) return value;
  if (ms % 3_600_000 === 0) return `${ms / 3_600_000}h`;
  if (ms % 60_000 === 0) return `${ms / 60_000}m`;
  if (ms % 1_000 === 0) return `${ms / 1_000}s`;
  return `${ms}ms`;
}

function statusFromBoolean(record: Record<string, unknown>): string | null {
  const running = pickBoolean(record, [
    'running',
    'activeRun',
    'state.running',
  ]);
  if (running === true) return 'running';
  const ok = pickBoolean(record, ['ok', 'success']);
  if (ok === true) return 'success';
  if (ok === false) return 'error';
  return null;
}

function formatJobBadge(job: CronJob): string {
  if (job.enabled === false) return 'disabled';
  if (job.status != null) return job.status;
  if (job.enabled === true) return 'active';
  return 'unknown';
}

function formatMaybeDate(value: string | null): string {
  if (value == null) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string' && error.trim().length > 0) return error;
  return fallback;
}

const Scroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const HeaderText = styled.div`
  min-width: 0;
`;

const Title = styled.h3`
  margin: 0 0 4px 0;
  color: var(--text-primary);
  font-size: 0.875rem;
  font-weight: 600;
`;

const Subtitle = styled.div`
  color: var(--text-secondary);
  font-size: 0.75rem;
  line-height: 1.35;
`;

const RefreshButton = styled.button`
  flex: 0 0 auto;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 5px 10px;
  border-radius: 6px;
  border: 1px solid var(--border-color);
  background: var(--bg-color-container);
  color: var(--text-primary);
  cursor: pointer;

  &:hover:not(:disabled) {
    background: var(--hover-bg);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

const Notice = styled.div`
  padding: 8px 10px;
  border-radius: 6px;
  background: color-mix(
    in srgb,
    var(--warning-color, #f5a623) 10%,
    transparent
  );
  border: 1px solid
    color-mix(in srgb, var(--warning-color, #f5a623) 30%, transparent);
  color: var(--text-primary);
  font-size: 0.75rem;
`;

const EmptyState = styled.div`
  color: var(--text-secondary);
  font-size: 0.8125rem;
  line-height: 1.45;
`;

const JobList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const JobCard = styled.div<{ readonly $selected: boolean }>`
  width: 100%;
  text-align: left;
  padding: 10px 12px;
  border: 1px solid
    ${({ $selected }) =>
      $selected
        ? 'color-mix(in srgb, var(--text-primary) 50%, var(--border-color))'
        : 'var(--border-color)'};
  border-radius: 7px;
  background: ${({ $selected }) =>
    $selected
      ? 'color-mix(in srgb, var(--hover-bg) 72%, transparent)'
      : 'color-mix(in srgb, var(--bg-color-container) 60%, transparent)'};
  color: inherit;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 8px;

  &:hover {
    background: var(--hover-bg);
  }
`;

const JobActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const InlineActionButton = styled.button`
  font-size: 0.6875rem;
  font-weight: 500;
  padding: 4px 8px;
  border-radius: 6px;
  border: 1px solid var(--border-color);
  background: var(--bg-color-container);
  color: var(--text-primary);
  cursor: pointer;

  &:hover {
    background: var(--hover-bg);
  }
`;

const JobTopLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const JobName = styled.div`
  min-width: 0;
  color: var(--text-primary);
  font-size: 0.8125rem;
  font-weight: 600;
  overflow-wrap: anywhere;
`;

const StatusBadge = styled.span<{ readonly $enabled: boolean | null }>`
  flex: 0 0 auto;
  border-radius: 999px;
  border: 1px solid
    ${({ $enabled }) =>
      $enabled === false
        ? 'color-mix(in srgb, var(--text-secondary) 30%, transparent)'
        : 'color-mix(in srgb, var(--success-color, #2ecc71) 35%, transparent)'};
  color: ${({ $enabled }) =>
    $enabled === false ? 'var(--text-secondary)' : 'var(--success-color)'};
  background: ${({ $enabled }) =>
    $enabled === false
      ? 'color-mix(in srgb, var(--text-secondary) 8%, transparent)'
      : 'color-mix(in srgb, var(--success-color, #2ecc71) 10%, transparent)'};
  font-size: 0.6875rem;
  line-height: 1;
  padding: 4px 7px;
`;

const JobDescription = styled.div`
  color: var(--text-secondary);
  font-size: 0.75rem;
  line-height: 1.35;
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
`;

const MetaItem = styled.div`
  min-width: 0;
`;

const MetaLabel = styled.div`
  color: var(--text-secondary);
  font-size: 0.625rem;
  font-weight: 600;
  text-transform: uppercase;
`;

const MetaValue = styled.div`
  color: var(--text-primary);
  font-size: 0.6875rem;
  line-height: 1.35;
  overflow-wrap: anywhere;
`;

const CommandLine = styled.div`
  color: var(--text-secondary);
  font-family: monospace;
  font-size: 0.6875rem;
  line-height: 1.4;
  overflow-wrap: anywhere;
`;

const RunsPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-top: 1px solid var(--border-color);
  padding-top: 12px;
`;

const PanelTitle = styled.div`
  color: var(--text-primary);
  font-size: 0.8125rem;
  font-weight: 600;
`;

const RunNotice = styled.div`
  color: var(--text-secondary);
  font-size: 0.75rem;
`;

const RunRow = styled.div`
  display: flex;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px solid
    color-mix(in srgb, var(--border-color) 65%, transparent);
`;

const RunStatus = styled.div`
  flex: 0 0 72px;
  color: var(--text-primary);
  font-size: 0.6875rem;
  font-weight: 600;
  overflow-wrap: anywhere;
`;

const RunBody = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const RunTime = styled.div`
  color: var(--text-secondary);
  font-size: 0.6875rem;
`;

const RunText = styled.div`
  color: var(--text-primary);
  font-size: 0.75rem;
  line-height: 1.35;
  overflow-wrap: anywhere;
`;

const RunError = styled(RunText)`
  color: var(--error-color, #e25c5c);
`;
